import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, Clock, Image as ImageIcon, Paperclip, Pencil, X } from 'lucide-react';
import { MentionInput, ScheduledAttachment, ScheduledMessage } from '../types';
import { Modal } from './Modal';
import { ConfirmDialog } from './ConfirmDialog';
import { ATTACHMENT_ONLY_TEXT } from './chat/MessageItem';
import { dbService } from '../services/dbService';
import { useUploadPolicy } from '../services/uploadPolicyService';
import { useToast } from '../contexts/ToastContext';
import { screenAttachmentFiles, pastedImageFiles } from '../utils/attachmentScreening';
import { formatFileSize } from '../utils/formatFileSize';
import { iconForFileName } from '../utils/fileIcon';
import { resolveServerUrl } from '../utils/serverUrl';

/**
 * 메시지 예약 — 작성(본문·사진·파일) + 보낼 시각 + 이 방의 예약 목록을 한 창에서 (카톡 PC '메시지 예약').
 *
 * - 진입: 전송 버튼 우클릭/길게 누름·클립 메뉴 '예약 전송'은 입력창의 글·첨부·멘션을 seed로 넘기고,
 *   입력창 위 '예약 메시지 N건' 칩은 빈 폼으로 연다. seed로 시작한 내용을 예약하면 호출부가 입력창을 비운다
 *   (onCreate의 fromSeed). 그냥 닫으면 입력창은 그대로다.
 * - 예약 성공 → 창은 열어 둔 채 폼만 비운다(새 예약이 바로 아래 목록에 뜨는 것이 확인이다).
 * - 목록의 '수정'은 그 예약을 위 폼으로 불러온다. 쓰던 내용은 잠시 넣어 두었다가 수정을 마치거나
 *   취소하면 되돌린다.
 * - 첨부는 '예약'/'저장'을 누를 때 올린다(입력창과 같은 시점 — 빼거나 닫은 첨부가 서버에 남지 않는다).
 *   예약 등록이 실패하면 방금 올린 파일만 지운다. 첨부만 있으면 일반 전송과 같은 대체 문구를 본문으로 보낸다.
 * - 멘션 자동완성은 없다(예전 수정 모달과 같은 규칙): 넘겨받은 멘션 중 본문에 '@이름'이 남은 것만 유지.
 * - 사전 차단(차단 확장자·용량·개수)은 입력창과 같은 screenAttachmentFiles. 최종 판정은 서버다.
 */

export interface ScheduleSeed {
  text: string;
  mentions: MentionInput[];
  files: Array<{ file: File; thumbnail?: string }>;
}

export interface ScheduleBody {
  content: string;
  mentions: MentionInput[];
  scheduledAt: string; // ISO
  attachments: ScheduledAttachment[];
}

interface ScheduleComposerModalProps {
  /** 대화방 이름 (제목에 붙인다) */
  channelTitle: string;
  /** 이 방의 내 예약 (대기 + 실패) — App의 myScheduled가 소스라 발송·취소가 실시간 반영된다 */
  items: ScheduledMessage[];
  seed: ScheduleSeed | null;
  /** false = 실패(토스트는 호출부) → 폼을 그대로 두어 재시도. fromSeed면 성공 시 호출부가 입력창을 비운다 */
  onCreate: (body: ScheduleBody, fromSeed: boolean) => Promise<boolean>;
  onUpdate: (id: string, body: ScheduleBody) => Promise<boolean>;
  /** 대기 = 취소, 실패 = 삭제 — 서버 DELETE 하나가 둘을 구분한다 */
  onCancelItem: (item: ScheduledMessage) => void;
  /**
   * 창이 떠 있는 동안 Tauri 네이티브 드롭을 받을 자리. 호출부의 드롭 리스너가
   * `(ref.current ?? 입력창 addFiles)(files)`로 보낸다 — 없으면 뒤에 가려진 입력창에 붙는다.
   */
  dropSinkRef: React.MutableRefObject<((files: File[]) => void) | null>;
  onClose: () => void;
}

type DraftFile =
  | { key: string; kind: 'local'; file: File; name: string; size: number; type: string; thumbnail?: string }
  | { key: string; kind: 'saved'; att: ScheduledAttachment };

interface FormState {
  text: string;
  files: DraftFile[];
  whenStr: string;
  fromSeed: boolean;
}

const MAX_AHEAD_MS = 30 * 24 * 60 * 60 * 1000;
const MIN_AHEAD_MS = 55 * 1000; // 서버 parseScheduledAt과 같은 5초 여유 (안내 문구는 "1분 뒤부터")

const pad = (n: number) => String(n).padStart(2, '0');
/** Date → datetime-local 값 (로컬 시각, 분 단위) */
const toLocalInput = (d: Date) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
/** datetime-local 값 → Date (로컬 시각). 일부 브라우저가 초를 붙여도 받는다 */
const fromLocalInput = (v: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/.exec(v);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]), 0, 0);
  return isNaN(d.getTime()) ? null : d;
};
/** 새 예약의 기본 시각 — 1시간 뒤(분 내림) */
const defaultWhen = () => {
  const d = new Date(Date.now() + 60 * 60 * 1000);
  d.setSeconds(0, 0);
  return toLocalInput(d);
};

const formatScheduledAt = (iso: string): string => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('ko-KR', {
    month: 'long', day: 'numeric', weekday: 'short', hour: 'numeric', minute: '2-digit',
  });
};

/** 수정 본문에 이름이 남은 멘션만 유지 — collectMentions와 같은 재스캔 규칙 */
const filterMentionInputsByText = (mentions: MentionInput[], text: string): MentionInput[] =>
  (mentions || []).filter(m => (m.type === 'all' ? text.includes('@all') : text.includes('@' + m.name)));

const attachmentsOf = (item: ScheduledMessage): ScheduledAttachment[] => item.attachments ?? [];

/** 첨부만 있는 예약의 본문은 대체 문구다 — 화면에서는 빈 본문으로 다룬다 */
const displayContent = (item: ScheduledMessage): string =>
  item.content === ATTACHMENT_ONLY_TEXT && attachmentsOf(item).length > 0 ? '' : item.content;

const newKey = () => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const fileName = (f: DraftFile) => (f.kind === 'local' ? f.name : f.att.name);
const fileSize = (f: DraftFile) => (f.kind === 'local' ? f.size : f.att.size);
const fileThumb = (f: DraftFile): string | undefined => {
  if (f.kind === 'local') return f.thumbnail;
  return f.att.type.startsWith('image/') ? resolveServerUrl(f.att.url) : undefined;
};

const shortTitle = (name: string) => (name.length > 20 ? `${name.slice(0, 20)}…` : name);

export const ScheduleComposerModal: React.FC<ScheduleComposerModalProps> = ({
  channelTitle,
  items,
  seed,
  onCreate,
  onUpdate,
  onCancelItem,
  dropSinkRef,
  onClose,
}) => {
  const { showToast } = useToast();
  const { retentionDays, maxFiles } = useUploadPolicy();

  const initialFiles = useMemo<DraftFile[]>(
    () => (seed?.files ?? []).map(({ file, thumbnail }) => ({
      key: newKey(),
      kind: 'local' as const,
      file,
      name: file.name,
      size: file.size,
      type: file.type || 'application/octet-stream',
      thumbnail,
    })),
    // 마운트 시 1회 — 창은 열 때마다 새로 마운트된다(seed는 그때 고정된다)
    []
  );

  const [text, setText] = useState(seed?.text ?? '');
  const [files, setFiles] = useState<DraftFile[]>(initialFiles);
  const [whenStr, setWhenStr] = useState(defaultWhen);
  const [fromSeed, setFromSeed] = useState(!!seed);
  const [editing, setEditing] = useState<ScheduledMessage | null>(null);
  // 수정을 시작할 때 쓰던 폼을 넣어 두는 자리 (렌더에 쓰지 않으므로 ref)
  const stashRef = useRef<FormState | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cancelTarget, setCancelTarget] = useState<ScheduledMessage | null>(null);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const topRef = useRef<HTMLDivElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // 개수 상한은 이미 붙은 첨부와 합산한다. 연달아 들어오는 드롭은 렌더 전이라 state가 낡았으므로 ref로 센다
  const filesCountRef = useRef(files.length);
  const submittingRef = useRef(false);
  useEffect(() => {
    filesCountRef.current = files.length;
  }, [files]);

  const addFiles = useCallback((incoming: File[]) => {
    if (submittingRef.current || incoming.length === 0) return; // 올리는 중에 붙은 파일은 이번 예약에 실리지 않는다
    const { accepted, messages } = screenAttachmentFiles(incoming, filesCountRef.current);
    messages.forEach(message => showToast({ type: 'error', message }));
    filesCountRef.current += accepted.length;
    accepted.forEach(file => {
      const key = newKey();
      setFiles(prev => [...prev, {
        key, kind: 'local', file, name: file.name, size: file.size, type: file.type || 'application/octet-stream',
      }]);
      // 미리보기는 사진만 (입력창과 같다 — 비이미지의 data URL은 어디서도 읽히지 않고 메모리만 먹는다)
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (ev) => {
          const thumbnail = ev.target?.result as string;
          setFiles(prev => prev.map(f => (f.key === key && f.kind === 'local' ? { ...f, thumbnail } : f)));
        };
        reader.readAsDataURL(file);
      }
    });
  }, [showToast]);

  // 창이 떠 있는 동안 Tauri 드롭을 받는다
  useEffect(() => {
    dropSinkRef.current = addFiles;
    return () => {
      dropSinkRef.current = null;
    };
  }, [addFiles, dropSinkRef]);

  const sorted = useMemo(
    () => [...items].sort((a, b) => new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime()),
    [items]
  );

  // setter만 쓰므로 identity가 고정된다 (finishEdit·아래 effect의 deps가 흔들리지 않게)
  const restoreForm = useCallback((state: FormState | null) => {
    setText(state?.text ?? '');
    setFiles(state?.files ?? []);
    setWhenStr(state?.whenStr ?? defaultWhen());
    setFromSeed(state?.fromSeed ?? false);
  }, []);

  const finishEdit = useCallback(() => {
    restoreForm(stashRef.current);
    stashRef.current = null;
    setEditing(null);
    setError(null);
  }, [restoreForm]);

  // 수정 중인 예약이 다른 기기에서 발송·취소되거나 실패로 바뀌면 수정을 푼다
  useEffect(() => {
    if (!editing) return;
    const current = items.find(i => String(i.id) === String(editing.id));
    if (!current || current.failedAt) {
      finishEdit();
      showToast({ type: 'info', message: '수정하던 예약이 이미 전송됐거나 취소됐습니다.' });
    }
  }, [items, editing, finishEdit, showToast]);

  const startEdit = (item: ScheduledMessage) => {
    if (submittingRef.current) return;
    // 쓰던 내용은 넣어 두었다가 수정을 마치면 되돌린다 (seed로 시작한 내용도 그대로 살아남는다)
    if (!editing) stashRef.current = { text, files, whenStr, fromSeed };
    setEditing(item);
    setText(displayContent(item));
    setFiles(attachmentsOf(item).map(att => ({ key: att.url, kind: 'saved' as const, att })));
    setWhenStr(toLocalInput(new Date(item.scheduledAt)));
    setError(null);
    topRef.current?.scrollIntoView({ block: 'start' });
    requestAnimationFrame(() => textareaRef.current?.focus({ preventScroll: true }));
  };

  const removeFile = (key: string) => {
    if (submittingRef.current) return;
    setFiles(prev => prev.filter(f => f.key !== key));
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const dt = e.clipboardData;
    if (!dt) return;
    // 글자가 있으면 글자가 이긴다(입력창 handlePaste와 같은 우선순위 — 엑셀은 표와 함께 비트맵도 싣는다)
    if ((dt.getData('text/plain') || '').trim().length > 0) return;
    const images = pastedImageFiles(dt);
    if (images.length > 0) {
      e.preventDefault();
      addFiles(images);
    }
  };

  const handleSubmit = async () => {
    if (submittingRef.current) return;
    const trimmed = text.trim();
    if (!trimmed && files.length === 0) {
      setError('보낼 메시지를 입력하거나 파일을 첨부해 주세요.');
      return;
    }
    const when = fromLocalInput(whenStr);
    if (!when) {
      setError('예약 시간 형식이 올바르지 않습니다.');
      return;
    }
    const now = Date.now();
    if (when.getTime() < now + MIN_AHEAD_MS) {
      setError('예약 시간은 1분 뒤부터 선택할 수 있습니다.');
      return;
    }
    if (when.getTime() > now + MAX_AHEAD_MS) {
      setError('예약 시간은 30일 안까지 선택할 수 있습니다.');
      return;
    }

    setError(null);
    submittingRef.current = true;
    setSubmitting(true);
    const done = () => {
      submittingRef.current = false;
      setSubmitting(false);
      setProgress(null);
    };

    // 1) 새 첨부 업로드 — 입력창 전송과 같은 시점(누를 때)
    const locals = files.filter((f): f is Extract<DraftFile, { kind: 'local' }> => f.kind === 'local');
    let uploaded: ScheduledAttachment[] = [];
    if (locals.length > 0) {
      try {
        setProgress(0);
        const res = await dbService.uploadFiles(locals.map(f => f.file), setProgress);
        uploaded = res.map(r => ({ name: r.name, url: r.url, size: r.size, type: r.type }));
      } catch (err) {
        done();
        setError(err instanceof Error && err.message ? err.message : '파일을 업로드하지 못했습니다. 잠시 후 다시 시도해 주세요.');
        return;
      }
    }
    // 폼에 보이는 순서 그대로 (서버는 받은 순서로 발송한다)
    let ui = 0;
    const attachments = files.map(f => (f.kind === 'saved' ? f.att : uploaded[ui++])).filter(Boolean);

    const content = trimmed || ATTACHMENT_ONLY_TEXT;
    const body: ScheduleBody = {
      content,
      mentions: editing
        ? filterMentionInputsByText(editing.mentions, content)
        : fromSeed ? filterMentionInputsByText(seed?.mentions ?? [], content) : [],
      scheduledAt: when.toISOString(),
      attachments,
    };

    let ok = false;
    try {
      ok = editing ? await onUpdate(editing.id, body) : await onCreate(body, fromSeed);
    } catch {
      ok = false;
    }
    if (!ok) {
      // 등록이 안 됐으니 방금 올린 파일만 치운다(재시도하면 다시 올린다). 실패해도 무시 — 서버가 참조 중이면 거부한다
      uploaded.forEach(a => {
        dbService.deleteFile(a.url).catch(() => {});
      });
      done();
      return;
    }

    done();
    if (editing) {
      finishEdit();
    } else {
      // 창은 열어 둔 채 폼만 비운다 — 새 예약은 바로 아래 목록에 나타난다
      restoreForm(null);
      requestAnimationFrame(() => textareaRef.current?.focus({ preventScroll: true }));
    }
  };

  const handleClose = () => {
    if (submittingRef.current) return; // 업로드는 중간에 멈출 수 없다
    onClose();
  };

  const retentionNotice = retentionDays > 0
    ? `첨부한 파일은 보낸 날부터 ${retentionDays}일 뒤 자동으로 삭제됩니다`
    : null;
  const minWhen = toLocalInput(new Date(Date.now() + 60 * 1000));
  const maxWhen = toLocalInput(new Date(Date.now() + MAX_AHEAD_MS));
  const submitLabel = submitting
    ? (progress != null && progress < 100 ? `올리는 중 ${progress}%` : editing ? '저장 중…' : '예약 중…')
    : editing ? '저장' : '예약';
  const canSubmit = !submitting && (text.trim().length > 0 || files.length > 0);

  return (
    <>
      <Modal
        isOpen
        onClose={handleClose}
        title={`메시지 예약 · ${shortTitle(channelTitle)}`}
        size="md"
        closeOnOverlay={false}
        // 기본 본문(60vh)이면 380×500 PC앱 창에서 목록이 거의 안 보인다 — 75vh로 키우고 폼+목록을 한 스크롤로
        bodyFlex
      >
        <div
          ref={topRef}
          className="space-y-4 flex-1 min-h-0 overflow-y-auto -mr-3 pr-3"
          // 웹: 창 위에 끌어다 놓은 파일을 받는다(창은 채팅창 드롭 영역 밖이라 안 받으면 브라우저가 파일을 연다)
          onDragOver={(e) => {
            if (!e.dataTransfer.types.includes('Files')) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = 'copy';
          }}
          onDrop={(e) => {
            if (!e.dataTransfer.types.includes('Files')) return;
            e.preventDefault();
            addFiles(Array.from(e.dataTransfer.files));
          }}
        >
          {editing && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-brand-100 text-brand-700">
              <Pencil size={14} className="shrink-0" aria-hidden="true" />
              <span className="flex-1 min-w-0 text-xs font-semibold truncate">
                {formatScheduledAt(editing.scheduledAt)} 예약 수정 중
              </span>
              <button
                type="button"
                onClick={finishEdit}
                disabled={submitting}
                className="shrink-0 px-2 py-1 text-xs font-semibold rounded-md hover:bg-brand-200 disabled:opacity-50 transition-colors"
              >
                수정 취소
              </button>
            </div>
          )}

          {/* 보낼 메시지 */}
          <div>
            <label htmlFor="schedule-composer-text" className="block text-xs font-semibold text-slate-600 mb-1.5">
              보낼 메시지
            </label>
            <textarea
              id="schedule-composer-text"
              ref={textareaRef}
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                if (error) setError(null);
              }}
              onPaste={handlePaste}
              rows={4}
              placeholder="예약할 메시지를 입력하세요"
              disabled={submitting}
              // text-base(16px): iOS는 16px 미만 입력란에 포커스하면 화면을 확대한다
              className="w-full px-3 py-2.5 rounded-lg bg-white border border-slate-200 text-base md:text-sm text-slate-700 resize-none focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent disabled:bg-slate-50"
            />
            <div className="mt-1 flex items-center gap-1">
              <button
                type="button"
                onClick={() => imageInputRef.current?.click()}
                disabled={submitting}
                aria-label="사진 첨부"
                title="사진 첨부"
                className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50 transition-colors"
              >
                <ImageIcon size={18} aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={submitting}
                aria-label="파일 첨부"
                title="파일 첨부"
                className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50 transition-colors"
              >
                <Paperclip size={18} aria-hidden="true" />
              </button>
              {files.length > 0 && (
                <span className="ml-auto text-xs text-slate-400">
                  {files.length} / {maxFiles}개
                </span>
              )}
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => {
                  if (e.target.files) addFiles(Array.from(e.target.files));
                  e.target.value = ''; // 같은 파일 다시 선택 가능
                }}
              />
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={(e) => {
                  if (e.target.files) addFiles(Array.from(e.target.files));
                  e.target.value = '';
                }}
              />
            </div>

            {files.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2 p-2 bg-slate-50 rounded-lg border border-slate-200">
                {files.map(f => {
                  const name = fileName(f);
                  const thumb = fileThumb(f);
                  const TypeIcon = iconForFileName(name);
                  return (
                    <div
                      key={f.key}
                      className="relative flex items-center gap-2 min-w-0 max-w-full bg-white border border-slate-200 rounded-lg p-2 pr-8"
                    >
                      {thumb ? (
                        <img src={thumb} alt={name} className="w-10 h-10 object-cover rounded shrink-0" />
                      ) : (
                        <div className="w-10 h-10 flex items-center justify-center bg-slate-100 rounded shrink-0">
                          <TypeIcon size={20} className="text-slate-400" aria-hidden="true" />
                        </div>
                      )}
                      <div className="flex flex-col min-w-0">
                        <span className="text-sm text-slate-700 truncate max-w-[140px]">{name}</span>
                        <span className="text-xs text-slate-400">{formatFileSize(fileSize(f))}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(f.key)}
                        disabled={submitting}
                        aria-label={`${name} 첨부 제거`}
                        className="absolute top-1 right-1 p-1.5 text-slate-400 hover:text-brand-600 hover:bg-brand-50 rounded disabled:opacity-50 transition-colors"
                      >
                        <X size={14} aria-hidden="true" />
                      </button>
                    </div>
                  );
                })}
                {retentionNotice && <p className="w-full text-xs text-slate-500">{retentionNotice}</p>}
              </div>
            )}
          </div>

          {/* 보낼 시각 */}
          <div>
            <label htmlFor="schedule-composer-when" className="block text-xs font-semibold text-slate-600 mb-1.5">
              보낼 시각
            </label>
            <div className="flex gap-2">
              <input
                id="schedule-composer-when"
                type="datetime-local"
                step={60}
                value={whenStr}
                min={minWhen}
                max={maxWhen}
                onChange={(e) => {
                  setWhenStr(e.target.value);
                  if (error) setError(null);
                }}
                disabled={submitting}
                className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-slate-200 text-base md:text-sm text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent disabled:bg-slate-50"
              />
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!canSubmit}
                className="shrink-0 min-w-[4.5rem] px-4 py-2 bg-brand-600 text-white text-sm font-semibold rounded-lg hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {submitLabel}
              </button>
            </div>
            <p className="mt-1.5 text-xs text-slate-400">1분 뒤부터 30일 안까지 선택할 수 있습니다</p>
            {error && <p role="alert" className="mt-1 text-xs text-brand-700">{error}</p>}
          </div>

          <hr className="border-slate-100" />

          {/* 예약 목록 */}
          <div>
            <h3 className="text-sm font-semibold text-slate-800">
              예약 목록{sorted.length > 0 && <span className="ml-1 text-slate-400 font-normal">{sorted.length}</span>}
            </h3>
            {sorted.length === 0 ? (
              <p className="py-6 text-center text-sm text-slate-500">예약한 메시지가 없습니다</p>
            ) : (
              <ul className="mt-1 divide-y divide-slate-100">
                {sorted.map(item => {
                  const failed = !!item.failedAt;
                  const isEditing = editing != null && String(editing.id) === String(item.id);
                  const body = displayContent(item);
                  const atts = attachmentsOf(item);
                  return (
                    <li
                      key={item.id}
                      className={`py-3 ${isEditing ? 'bg-brand-100 -mx-2 px-2 rounded-lg' : ''}`}
                    >
                      <div className="flex items-center gap-1.5 text-xs font-semibold">
                        {failed ? (
                          <AlertCircle size={14} className="text-brand-700 shrink-0" aria-hidden="true" />
                        ) : (
                          <Clock size={14} className="text-slate-400 shrink-0" aria-hidden="true" />
                        )}
                        <span className={failed ? 'text-brand-700' : 'text-slate-600'}>
                          {formatScheduledAt(item.scheduledAt)}
                        </span>
                        {isEditing && <span className="text-brand-700">· 수정 중</span>}
                      </div>
                      {body && (
                        <p className="mt-1 text-sm text-slate-700 whitespace-pre-wrap break-anywhere line-clamp-2">
                          {body}
                        </p>
                      )}
                      {atts.length > 0 && (
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                          {atts.map(att => {
                            const Icon = att.type.startsWith('image/') ? ImageIcon : iconForFileName(att.name);
                            return (
                              <span
                                key={att.url}
                                className="inline-flex items-center gap-1 max-w-full px-2 py-0.5 rounded-md bg-slate-100 text-xs text-slate-600"
                              >
                                <Icon size={12} className="shrink-0 text-slate-400" aria-hidden="true" />
                                <span className="truncate max-w-[160px]">{att.name}</span>
                              </span>
                            );
                          })}
                        </div>
                      )}
                      {failed && item.failReason && (
                        <p className="mt-1 text-xs text-brand-700">{item.failReason}</p>
                      )}
                      <div className="mt-2 flex gap-2">
                        {!failed && !isEditing && (
                          <button
                            type="button"
                            onClick={() => startEdit(item)}
                            disabled={submitting}
                            className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 disabled:opacity-50 transition-colors"
                          >
                            수정
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => (failed ? onCancelItem(item) : setCancelTarget(item))}
                          disabled={submitting}
                          className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-white border border-brand-600 text-brand-600 hover:bg-brand-50 disabled:opacity-50 transition-colors"
                        >
                          {failed ? '삭제' : '취소'}
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </Modal>

      {cancelTarget && (
        <ConfirmDialog
          isOpen
          title="예약 취소"
          message="이 메시지의 예약 전송을 취소할까요? 메시지는 전송되지 않습니다."
          confirmLabel="예약 취소"
          danger
          onConfirm={() => {
            // 수정 중이던 예약을 직접 취소했으면 조용히 수정을 푼다('다른 기기에서 취소됨' 안내가 뜨지 않게)
            if (editing && String(editing.id) === String(cancelTarget.id)) finishEdit();
            onCancelItem(cancelTarget);
            setCancelTarget(null);
          }}
          onCancel={() => setCancelTarget(null)}
        />
      )}
    </>
  );
};
